import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { trpc } from '~/utils/trpc';
import { useAuth } from '~/lib/auth-context';

export interface FeatureFlags {
  useTanStackStart: boolean; // Feature flag to enable TanStack Start app
  // Candidate ↔ employer data-sharing (public résumé toggle, discoverable-
  // by-employers profile, etc.). LEGALLY GATED behind Spanish agencia de
  // colocación authorization (Ley 3/2023 Art. 43). Default OFF until
  // ABELLAN LABS SLU files the declaración responsable — flip on via the
  // admin panel once the ETC registration lands. See B-new-27/28 in the
  // roadmap and docs/04-development/EMPLOYER-PORTAL-DESIGN.md.
  candidateDiscoverability: boolean;
  // Company Deep Dive (40cr / €39). OFF until the enrichment layer works —
  // four of its five services call SerpApi engines that do not exist, so every
  // company currently scores identically (see the note in
  // routes/$locale/dashboard.reports.lazy.tsx). Same flag the API gate reads
  // (routers/reports.ts), so one switch controls both layers.
  companyDeepDive: boolean;
  // B-new-2855: employer portal hidden in prod until the operator tests it.
  // Same DB flag the API gate (company.* / employerJobs.*) and the Pages edge
  // function read. Default OFF.
  employerPortal: boolean;
  // B-new-9655: public indexing of logged-out /browse + job pages. Edge-driven;
  // the client copy is defence-in-depth only. Default OFF.
  publicBrowseIndexing: boolean;
  [key: string]: boolean; // Allow for additional feature flags
}

interface FeatureFlagContextType {
  flags: FeatureFlags;
  isEnabled: (flag: keyof FeatureFlags) => boolean;
  isLoading: boolean;
}

// Fail-closed defaults, used while the backend fetch is in flight or if it
// fails. candidateDiscoverability specifically MUST resolve to `false` in
// that window — it has real legal stakes (Ley 3/2023 Art. 43), unlike a
// normal convenience flag where fail-open would just be a minor UX blip.
const FAIL_CLOSED_FLAGS: FeatureFlags = {
  useTanStackStart: false,
  candidateDiscoverability: false,
  companyDeepDive: false,
  employerPortal: false,
  publicBrowseIndexing: false,
};

// Flags excluded from the localStorage same-session cache — a stale cached
// `true` surviving after an admin revokes it would be a compliance gap,
// not a UX inconvenience, so this one always waits for a live fetch.
const NEVER_CACHE = new Set<keyof FeatureFlags>([
  'candidateDiscoverability',
  // Money path: a stale cached `true` would re-expose a chargeable report
  // after it was switched off, which is a billing problem rather than a UX one.
  'companyDeepDive',
  // B-new-2855: a stale cached `true` must not re-expose the portal after a flip off.
  'employerPortal',
  // B-new-9655: a stale cached `true` must not keep pages indexable after a flip off.
  'publicBrowseIndexing',
]);

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  // `feature_flags` (migration 0047) is the source of truth — see
  // apps/api/src/routers/feature-flags.ts. localStorage is a same-session
  // cache/fallback for perceived performance on repeat loads, never the
  // source of truth.
  // B-new-100: `featureFlags.list` is a protectedDbProcedure. Without this gate
  // the provider fired an authenticated, DB-initialising request — and took a
  // 401 — on every page load for every signed-out visitor, including /sign-in,
  // /sign-up and the 404 splat. `enabled` is the documented way to defer a
  // query until auth is ready; TanStack Query checks it before every fetch
  // decision, so the request is never issued rather than issued-and-discarded.
  //
  // A disabled query reports isLoading === false in v5 (isLoading is defined as
  // isPending && isFetching, and a disabled query never fetches), so signed-out
  // consumers read "not loading" with FAIL_CLOSED_FLAGS rather than hanging on
  // a pending state that will never resolve.
  const { user } = useAuth();
  const { data, isLoading, isError } = trpc.featureFlags.list.useQuery(undefined, {
    staleTime: 60_000,
    enabled: !!user,
  });

  const [cached, setCached] = useState<Partial<FeatureFlags>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = localStorage.getItem('featureFlags');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!data || isError) return;
    const next: Partial<FeatureFlags> = {};
    for (const row of data) {
      if (!NEVER_CACHE.has(row.key as keyof FeatureFlags)) {
        next[row.key] = row.enabled;
      }
    }
    setCached(next);
    try {
      localStorage.setItem('featureFlags', JSON.stringify(next));
    } catch {
      // Best-effort — private browsing / storage-full. Cache is a
      // fallback only, so a write failure here is not user-visible.
    }
  }, [data, isError]);

  const flags: FeatureFlags = { ...FAIL_CLOSED_FLAGS };
  for (const [key, value] of Object.entries(cached)) {
    if (typeof value === 'boolean') flags[key] = value;
  }
  if (data && !isError) {
    for (const row of data) {
      flags[row.key] = row.enabled;
    }
  }

  const isEnabled = (flag: keyof FeatureFlags): boolean => flags[flag] ?? false;

  return (
    <FeatureFlagContext.Provider value={{ flags, isEnabled, isLoading }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Fallback served when a flag hook is called outside a mounted provider.
// B-new-78: this used to THROW, which white-screened the paid
// /dashboard/reports hub for every signed-in user the whole time the
// provider went unmounted. A missing provider must degrade to the same
// fail-closed flags the provider itself serves on a failed fetch —
// crashing the page is strictly worse than hiding a gated section.
const MISSING_PROVIDER_FALLBACK: FeatureFlagContextType = {
  flags: FAIL_CLOSED_FLAGS,
  isEnabled: (flag) => FAIL_CLOSED_FLAGS[flag] ?? false,
  isLoading: false,
};

export const useFeatureFlag = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    console.error(
      'useFeatureFlag called outside FeatureFlagProvider — serving fail-closed flags. Mount FeatureFlagProvider in main.tsx.'
    );
    return MISSING_PROVIDER_FALLBACK;
  }
  return context;
};

// Helper hook for specific flags
export const useFeatureFlagEnabled = (flag: keyof FeatureFlags): boolean => {
  const { isEnabled } = useFeatureFlag();
  return isEnabled(flag);
};